// Shape and rules of a template form, kept out of the component.
//
// Both the create panel and the edit card drive the same state through this, so
// "what a blank template is" and "what gets sent" have one definition. They used
// to have two, and they disagreed: the create form defaulted `enabled` to false
// while the model defaults it to true, so every template made in the SPA saved
// inert with a dead Deploy button.

import { LootPool, Template } from './templatesApi'

// Placeholders the backend accepts in server_name_template. Closed set: a typo
// is a validation error server-side, and echoing that here means the author
// finds out while typing instead of on submit.
export const NAME_PLACEHOLDERS = ['slot', 'index', 'region', 'zone', 'rank', 'map', 'map_name', 'fee']

// Matches gameapi.models.server_profile.SAMPLE_CONTEXT, which is what the
// backend renders against when it length-checks the template.
export const SAMPLE_CONTEXT: Record<string, string> = {
    slot: 'us-001',
    index: '1',
    region: 'US',
    // The short zone (NA/SA/EU/SEA), the rank read off the requirements, and
    // the map as people say it. Mirrors the backend's SAMPLE_CONTEXT.
    zone: 'NA',
    rank: 'PUBLIC',
    map: 'Military Base',
    map_name: 'MilitaryBase_v3',
    fee: '1.00',
}

// GameServer.name is CharField(max_length=64).
export const NAME_MAX = 64

// Values that mean "no limit" when left empty; the backend maps "" to null for
// these, so an empty box is a legitimate thing to send.
const BLANKABLE = new Set([
    'fee',
    'max_players',
    'password',
    'min_sessions',
    'max_sessions',
    'min_kills',
    'max_kills',
    'slot_start',
    'slot_end',
])

// Numbers with a server-side default. An empty box here means "leave it alone"
// rather than zero -- sending 0 spawn points would silently disable loot, and
// that is exactly what the old `Number(e.target.value) || 0` did to anyone who
// cleared the field to retype it.
const OPTIONAL_NUMBERS = ['loot_update_frequency', 'loot_max_spawn_points']

// Named ...State rather than TemplateForm so it cannot collide with the
// TemplateForm.tsx component: two modules differing only in case resolve to
// whichever the filesystem prefers, and on Windows and macOS that is not the
// one you meant.
export type TemplateFormState = ReturnType<typeof blankForm>

export const blankForm = () => ({
    name: '',
    // True, matching the model. A form that quietly saves something inert is
    // how "I made a template and Deploy does nothing" happens.
    enabled: true,
    activate_on_apply: true,
    deploy_slot_regions: [] as string[],
    // Region and index only. The client receives fee, map and visibility over
    // the API and renders them in their own columns, so repeating them here is
    // duplication that goes stale the moment a server is re-priced.
    server_name_template: '{region} {index}',
    fee: '',
    max_players: '20',
    map_id: 6,
    password: '',
    check_requirements: false,
    min_sessions: '',
    max_sessions: '',
    min_kills: '',
    max_kills: '',
    loot_update_frequency: '300',
    loot_max_spawn_points: '20',
    loot_pools: [] as LootPool[],
    tournaments: [] as number[],
})

const str = (v: any) => (v === null || v === undefined ? '' : String(v))

/** An existing template as form state. Everything is a string while editing. */
export const formFrom = (template: Template): TemplateFormState => ({
    ...blankForm(),
    name: template.name,
    enabled: template.enabled,
    activate_on_apply: template.activate_on_apply,
    deploy_slot_regions: [...(template.deploy_slot_regions ?? [])],
    server_name_template: template.server_name_template,
    fee: str(template.fee),
    max_players: str(template.max_players),
    map_id: template.map_id,
    password: str(template.password),
    check_requirements: template.check_requirements,
    min_sessions: str(template.min_sessions),
    max_sessions: str(template.max_sessions),
    min_kills: str(template.min_kills),
    max_kills: str(template.max_kills),
    loot_update_frequency: str(template.loot_update_frequency),
    loot_max_spawn_points: str(template.loot_max_spawn_points),
    loot_pools: template.loot_pools.map((p) => ({ ...p })),
    tournaments: template.tournaments.map((t) => t.id),
})

/**
 * Form state as a request body.
 *
 * Empty optional numbers are omitted rather than sent: write_template skips
 * fields absent from the payload, so leaving one out keeps whatever the server
 * already has instead of overwriting it with a guess.
 */
export const payloadOf = (form: TemplateFormState): Record<string, any> => {
    const out: Record<string, any> = {}
    for (const [key, value] of Object.entries(form)) {
        if (OPTIONAL_NUMBERS.includes(key)) {
            const text = String(value).trim()
            if (text === '') continue
            const n = Number(text)
            out[key] = Number.isFinite(n) ? n : text
            continue
        }
        if (BLANKABLE.has(key)) {
            out[key] = String(value).trim()
            continue
        }
        out[key] = value
    }
    return out
}

/**
 * Rendered server name, as close to the real thing as can be known.
 *
 * Half these placeholders come from the *server* and half from the *template*,
 * and only the first half are genuinely unknowable while authoring:
 *
 * - slot and index belong to a server that does not exist yet → sample values.
 * - map_name and fee are the template's own settings → use them. Showing
 *   samples for those was actively misleading: set the map to
 *   AbandonedRadioStation at $0.10, get shown "MilitaryBase_v3 1.00", and
 *   conclude the placeholders are broken. They were not; this was.
 * - region is the first region the template covers, when it covers any.
 */
export const previewName = (
    template: string,
    actual?: { mapName?: string; fee?: string; region?: string; zone?: string; rank?: string; map?: string }
) => {
    const unknown: string[] = []
    const rendered = (template || '').replace(/\{([^}]*)\}/g, (_match, raw) => {
        // Format specs are allowed server-side ("{fee:.2f}"), so compare on the
        // field name alone or every formatted placeholder reads as a typo.
        const parts = String(raw).split(/[:!]/)
        const field = parts[0]
        if (!NAME_PLACEHOLDERS.includes(field)) {
            unknown.push(field)
            return `{${raw}}`
        }
        // The fee is money and always renders to two places, whether it was
        // written as {fee} or {fee:.2f}. Showing an unrounded value here would
        // promise a name the backend does not produce — which is how the
        // preview came to say "1.00" while servers were named "0.1".
        if (field === 'fee') {
            const spec = parts[1] ?? ''
            const places = /\.(\d+)f/.exec(spec)
            // Named apart from the callback's `raw` to avoid shadowing it.
            const typed = actual?.fee?.trim()
            // An empty fee box means a free server, and the name renders blank
            // for it — same as the backend.
            if (actual && !typed) return ''
            const value = typed !== undefined ? Number(typed) : Number(SAMPLE_CONTEXT.fee)
            if (!Number.isFinite(value)) return typed ?? ''
            return value.toFixed(places ? Number(places[1]) : 2)
        }
        if (field === 'map_name' && actual?.mapName) return actual.mapName
        if (field === 'region' && actual?.region) return actual.region
        if (field === 'zone' && actual?.zone) return actual.zone
        if (field === 'rank' && actual?.rank) return actual.rank
        if (field === 'map' && actual?.map) return actual.map
        return SAMPLE_CONTEXT[field]
    })
    return { rendered, unknown, tooLong: rendered.length > NAME_MAX }
}

/**
 * Django's flattened validation errors back into a per-field map.
 *
 * error_response() joins them as "field: message; field: message", which the
 * form previously showed whole in a three-second corner toast — unreadable for
 * a form this size, and gone before it could be re-read.
 */
export const parseFieldErrors = (message: string): Record<string, string> => {
    const errors: Record<string, string> = {}
    for (const part of (message || '').split('; ')) {
        const at = part.indexOf(': ')
        if (at <= 0) continue
        const field = part.slice(0, at).trim()
        // Only treat it as a field if it looks like one; a plain sentence
        // containing a colon must not become a phantom error on a made-up key.
        if (!/^[a-z_][a-z0-9_]*$/.test(field)) continue
        errors[field] = part.slice(at + 2).trim()
    }
    return errors
}
